import React from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';

/**
 * @component
 * @see {@link https://www.telerik.com/kendo-react-ui/components/dropdowns/dropdownlist| KendoReact DropDownList Documentation}
 *
 * @description Select - A reusable Kendo DropDownList component.
 *
 * @param {Array} options - Array of options, each with { value, label }.
 * @param {Object} value - The selected value object.
 * @param {Function} onChange - Callback when selection changes.
 * @param {string|object} defaultItem - The placeholder/default item (should match option shape).
 * @param {boolean} disabled - Disable the select.
 * @param {string} className - Additional CSS classes.
 * @param {string} fillMode - Kendo fillMode ("solid", "flat", "outline", null).
 * @param {string} size - Kendo size ("small", "medium", "large", null).
 * @param {string} rounded - Kendo rounded ("small", "medium", "large", "full", null).
 * @returns {JSX.Element}
 *
 * @example
 * <Select
 *   options={[{ value: 1, label: 'One' }]}
 *   value={{ value: 1, label: 'One' }}
 *   onChange={val => setValue(val)}
 *   defaultItem={{ label: "Select an option", value: null }}
 *   fillMode="flat"
 *   size="small"
 *   rounded="large"
 * />
 */
export const Select = ({
  options = [],
  value,
  onChange,
  defaultItem,
  disabled = false,
  className = '',
  fillMode = 'solid',
  size = 'medium',
  rounded = 'medium',
  ...rest
}) => (
  <DropDownList
    data={options}
    value={value}
    textField="label"
    dataItemKey="value"
    onChange={e => onChange(e.target.value)}
    defaultItem={defaultItem}
    disabled={disabled}
    className={className}
    fillMode={fillMode}
    size={size}
    rounded={rounded}
    style={{ width: '100%' }}
    {...rest}
  />
);